import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Row, Col, Button, message, InputNumber, Space, Spin, Tag, Modal, Table, Empty, Select, DatePicker, Divider, Alert, Popover, Collapse,
} from 'antd';
import {
  BookOutlined, SaveOutlined, ReloadOutlined, FundOutlined, WarningOutlined, RiseOutlined, FallOutlined, SwapOutlined, ThunderboltOutlined, AimOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import { apiFetch } from '../utils/api.js';
import Overview from './Overview.jsx';
import EarnGoalModal from '../components/EarnGoalModal.jsx';
import EarnGoalCard from '../components/EarnGoalCard.jsx';

// ===== 格式化工具 =====
const fmtMoney = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0.00';
  const neg = n < 0;
  const abs = Math.abs(n).toFixed(2);
  const parts = abs.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + parts.join('.');
};
const wan = (v) => (Number(v) / 10000).toFixed(1) + '万';
const fmtYuan = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '¥0.00';
  const neg = n < 0;
  const abs = Math.abs(n).toFixed(2);
  const parts = abs.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-¥' : '¥') + parts.join('.');
};
const pct = (v) => (Number(v) * 100).toFixed(1) + '%';
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// ===== 糖果风配色 =====
const CORAL = '#D85A30';
const INK = '#26201C';
const C = { blue: '#5B8FF9', green: '#3F8600', red: '#D85A30', amber: '#F5A623', purple: '#9270CA', teal: '#13C2C2', gray: '#bfbfbf' };

// 公司账本默认结构（与后端 ledgerDefaults 对齐）
const DEFAULT_LEDGER = {
  balanceSheet: {
    流动资产: { 货币资金: 0, 微信客户应收: 0, 平台应收: 0, 存货: 0 },
    非流动资产: { 固定资产: 0, 无形资产: 0, 其他非流动资产: 0 },
    流动负债: { 短期借款: 0, 应付账款: 0, 应付工资: 0, 其他流动负债: 0 },
    非流动负债: { 长期借款: 0, 其他非流动负债: 0 },
    所有者权益: { 实收资本: 0, 未分配利润: 0 },
  },
  cashFlow: { 经营活动现金流净额: 0, 投资活动现金流净额: 0, 筹资活动现金流净额: 0, 期初现金: 0, 期末现金: 0 },
  opex: { 人工: 0, 房租: 0, 仓储物流: 0, 税费: 0, 其他经营费用: 0 },
  cogs: null,
  openingPayable: 0, // 上月未付应付账款（历史遗留/期初结转）
};

// ===== ECharts 轻量封装（原生 echarts，避开 echarts-for-react 打包坑） =====
function EChart({ option, height = 260 }) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const c = echarts.init(ref.current);
    chartRef.current = c;
    const onResize = () => c.resize();
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); c.dispose(); chartRef.current = null; };
  }, []);
  useEffect(() => {
    if (chartRef.current && option) chartRef.current.setOption(option, true);
  }, [option]);
  return <div ref={ref} style={{ width: '100%', height }} />;
}

// ===== 通用卡片 =====
function BoardCard({ title, desc, children, style }) {
  return (
    <Card size="small" style={{ borderRadius: 10, height: '100%', boxShadow: '0 1px 4px rgba(216,90,48,.06)', ...style }}
      styles={{ body: { padding: 16 } }}>
      <div style={{ fontWeight: 600, fontSize: 15, color: INK, marginBottom: 2 }}>{title}</div>
      {desc ? <div style={{ fontSize: 12, color: '#A08C82', marginBottom: 10, lineHeight: 1.6 }}>{desc}</div> : null}
      {children}
    </Card>
  );
}

// ===== KPI 卡 =====
function Kpi({ label, value, delta, deltaText, sub }) {
  const up = delta != null && delta >= 0;
  const color = delta == null ? INK : (up ? CORAL : C.green);
  return (
    <Card size="small" className="kpi-tap" style={{ borderRadius: 10, borderLeft: `3px solid ${CORAL}`, boxShadow: '0 1px 4px rgba(216,90,48,.06)' }} styles={{ body: { padding: '14px 16px' } }}>
      <div style={{ fontSize: 13, color: '#8C8C8C' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, marginTop: 6, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
        {delta != null && (
          <span style={{ color, fontWeight: 600 }}>{up ? '▲' : '▼'} {pct(delta)}</span>
        )}{(delta != null && deltaText) ? <span> {deltaText}</span> : null}
      </div>
      {sub ? <div style={{ fontSize: 11, color: '#B3A79F', marginTop: 3, lineHeight: 1.5 }}>{sub}</div> : null}
    </Card>
  );
}

// ===== 排行榜行 =====
function RankRow({ rank, name, value, valueColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #F5EDE8' }}>
      <span style={{ width: 20, height: 20, borderRadius: 5, background: rank <= 3 ? CORAL : '#F3E2D8', color: rank <= 3 ? '#fff' : '#A08C82', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{rank}</span>
      <span style={{ flex: 1, fontSize: 13, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: valueColor, flexShrink: 0 }}>{value}</span>
    </div>
  );
}

export default function DecisionBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [earnOpen, setEarnOpen] = useState(false);
  const [ledgerMonth, setLedgerMonth] = useState(null);
  const [ledgerForm, setLedgerForm] = useState(null);
  const [ledgerAuto, setLedgerAuto] = useState(null);      // 自动预填建议（存货/本月净利/累计净利）
  const [ledgerPrefill, setLedgerPrefill] = useState(null); // { auto, prev } 用于「按建议重填」
  const [ledgerHasSaved, setLedgerHasSaved] = useState(false); // 该月是否已有快照
  const [savingLedger, setSavingLedger] = useState(false);
  // 月度对比状态
  const [cmpA, setCmpA] = useState(''); // 对比基准月
  const [cmpB, setCmpB] = useState(''); // 对比目标月
  // 查看月份（KPI / 全渠道利润总汇 / 各店明细 / 支出结构 / 资产负债跟随；趋势图与月度对比不受影响）
  const [viewMonth, setViewMonth] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch('/api/decision-board').then(r => r.json());
      if (d.success) setData(d.data);
      else message.error(d.error || '加载失败');
    } catch (e) { message.error('公司看板加载失败'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // 月度对比：默认对比月 = 最新订单发货月 vs 上月（订单发货口径，独立于账单录入）
  useEffect(() => {
    if (!data || !data.orderMonths || data.orderMonths.length === 0) return;
    const om = data.orderMonths;
    const n = om.length;
    if (n >= 2) { setCmpA(om[n - 2]); setCmpB(om[n - 1]); }
    else if (n === 1) { setCmpA(om[0]); setCmpB(om[0]); }
  }, [data]);

  // 查看月份：默认最新财务月；刷新后若所选月仍存在则保留用户选择
  useEffect(() => {
    if (!data || !data.months || data.months.length === 0) return;
    setViewMonth(prev => (prev && data.months.includes(prev)) ? prev : data.months[data.months.length - 1]);
  }, [data]);

  // ===== 对账本 form 应用实时自动项（现金/存货/应收/应付/净利/现金流），保留手填项不动 =====
  const applyAuto = (f, auto) => {
    if (!auto) return f;
    const nf = JSON.parse(JSON.stringify(f));
    nf.balanceSheet['流动资产']['存货'] = auto.invTotal || 0;
    nf.balanceSheet['流动资产']['微信客户应收'] = auto.wxReceivable || 0;
    nf.balanceSheet['所有者权益']['未分配利润'] = Math.round(((auto.cumProfit || 0)) * 100) / 100;
    // 货币资金/期末现金 = 资金中心实时余额（自动，2026-09-25 起，不再手填月末实点）
    nf.balanceSheet['流动资产']['货币资金'] = auto.cashBalance || 0;
    nf.cashFlow['期末现金'] = auto.cashBalance || 0;
    // 应付账款 = 上月未付结转 + 应付账期模块未付（自动，2026-09-25 起）
    const openingPayable = Number(auto.prevPayable || 0);
    nf.openingPayable = Math.round(openingPayable * 100) / 100;
    nf.balanceSheet['流动负债']['应付账款'] = Math.round((openingPayable + (auto.payableUnpaid || 0)) * 100) / 100;
    // 经营现金流倒挤 = 期末 − 期初 − 投资 − 筹资
    nf.cashFlow['经营活动现金流净额'] = Math.round(((Number(nf.cashFlow['期末现金']) || 0) - (Number(nf.cashFlow['期初现金']) || 0) - (Number(nf.cashFlow['投资活动现金流净额']) || 0) - (Number(nf.cashFlow['筹资活动现金流净额']) || 0)) * 100) / 100;
    return nf;
  };

  // ===== 月末快照预填：自动项（存货=库存成本、未分配利润=累计净利、期初现金=上月末）+ 其余结转上月 =====
  const buildPrefill = (auto, prev, def) => {
    let f = JSON.parse(JSON.stringify(def || DEFAULT_LEDGER));
    if (prev && prev.balanceSheet) {
      for (const g of Object.keys(f.balanceSheet)) {
        for (const k of Object.keys(f.balanceSheet[g])) {
          const v = prev.balanceSheet[g] && prev.balanceSheet[g][k];
          if (v != null) f.balanceSheet[g][k] = v;
        }
      }
      if (prev.cashFlow) f.cashFlow['期初现金'] = prev.cashFlow['期末现金'] || 0;
    }
    return applyAuto(f, auto);
  };

  // ===== 打开公司账本编辑 =====
  const openLedger = async (month) => {
    const m = month || (data && data.months.length ? data.months[data.months.length - 1] : dayjs().format('YYYY-MM'));
    setLedgerMonth(m);
    setLedgerOpen(true);
    setLedgerForm(null);
    try {
      const d = await apiFetch('/api/company-ledger?month=' + encodeURIComponent(m)).then(r => r.json());
      if (d.success) {
        const existed = (d.months || []).includes(m);
        setLedgerAuto(d.auto || null);
        setLedgerPrefill({ auto: d.auto || null, prev: d.prev || null });
        if (existed) {
          setLedgerHasSaved(true);
          const saved = JSON.parse(JSON.stringify(d.data || DEFAULT_LEDGER));
          if (saved.openingPayable == null) saved.openingPayable = 0; // 旧快照无「上月未付」字段兜底
          setLedgerForm(saved);
        } else {
          setLedgerHasSaved(false);
          setLedgerForm(buildPrefill(d.auto, d.prev, d.defaults || DEFAULT_LEDGER));
        }
      }
    } catch (e) { setLedgerForm(JSON.parse(JSON.stringify(DEFAULT_LEDGER))); }
  };

  const applyPrefill = () => {
    if (!ledgerPrefill) return;
    // 基于当前表单（保留手填项：平台应收/借款/实收资本等），只刷新自动项
    setLedgerForm(f => applyAuto(f, ledgerPrefill.auto));
    message.info('已按实时数据重填自动项（现金/存货/应收/应付/净利），请核对后保存');
  };

  const saveLedger = async () => {
    setSavingLedger(true);
    try {
      const r = await apiFetch('/api/company-ledger', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: ledgerMonth, ...ledgerForm }),
      }).then(x => x.json());
      if (r.success) { message.success(`${ledgerMonth} 公司账本已保存`); setLedgerOpen(false); load(); }
      else message.error(r.error || '保存失败');
    } catch (e) { message.error('保存失败'); }
    setSavingLedger(false);
  };

  if (loading) return <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" tip="加载公司看板..." /></div>;

  const months = (data && data.months) || [];
  const orderMonths = (data && data.orderMonths) || [];
  const records = (data && data.records) || [];
  const products = (data && data.products) || [];
  const ledgerMonths = (data && data.ledgerMonths) || {};
  const billSummary = (data && data.billSummary) || {};
  const completeness = (data && data.completeness) || {};
  const wechat = (data && data.wechat) || {};
  const allChannel = (data && data.allChannel) || {};

  if (!months.length) {
    return (
      <div className="page-container">
        <Card style={{ borderRadius: 12 }}>
          <Empty description="暂无数据：请先在「财务管理 → 账务流水」导入拼多多账单，公司账本（资产负债/现金流）可选填" />
          <div style={{ textAlign: 'center', marginTop: 8 }}><Button type="primary" onClick={load}>刷新</Button></div>
        </Card>
      </div>
    );
  }

  const last = months.length - 1;
  // 查看月索引（viewMonth 未就绪时回退最新月）
  const vi = months.indexOf(viewMonth) >= 0 ? months.indexOf(viewMonth) : last;
  // ===== 财务口径（净入账，账务流水 ledgerFlow 单一数据源）=====
  const netIn = months.map(m => (billSummary[m] ? billSummary[m].netIncome : 0));   // 净入账（到账可调配）
  const costArr = months.map(m => (billSummary[m] ? billSummary[m].costTotal : 0)); // 商品成本
  const adArr = months.map(m => (billSummary[m] ? billSummary[m].adSpendUsed : 0)); // 推广费
  const gross = months.map(m => (billSummary[m] ? billSummary[m].gross : 0));       // 毛利 = 净入账 − 商品成本
  const profit = months.map(m => (billSummary[m] ? billSummary[m].netProfit : 0));  // 净利 = 毛利 − 经营费用合计（账务流水口径，已含推广费+运费+人工+房租+税费+辅材）
  const byMonth = (field) => months.map(m => records.filter(r => r['月份'] === m).reduce((s, r) => s + (Number(r[field]) || 0), 0));
  const tech = byMonth('技术服务费'), pay = byMonth('支付手续费'), penalty = byMonth('罚款赔付'), refund = byMonth('退款');
  const grow = (arr) => arr.map((v, i) => (i === 0 ? null : (arr[i - 1] !== 0 ? (v - arr[i - 1]) / Math.abs(arr[i - 1]) : 0)));

  // ===== 微信客户（发货口径，与拼多多到账口径分开；与最新财务月对齐）=====
  const wxData = (wechat.byMonth && wechat.byMonth[months[last]]) || null;
  const wxProfit = wxData ? wxData.profit : 0;
  const wxRevenue = wxData ? wxData.revenue : 0;
  const wxCost = wxData ? wxData.cost : 0;
  const wxFee = wxData ? round2(wxData.freight + wxData.packaging) : 0;
  const pddOpex = (billSummary[months[last]] && billSummary[months[last]].opexDetail) || {};
  const pddFee = round2((Number(pddOpex['运费']) || 0) + (Number(pddOpex['辅材及其他']) || 0));
  const allChannelProfit = round2(profit[last] + wxProfit); // 全渠道合计（混合口径，仅供参考）
  // ===== 查看月（vi）口径的微信 / 拼多多费用派生：供 KPI、利润总汇、各店明细跟随查看月份 =====
  const wxDataV = (wechat.byMonth && wechat.byMonth[months[vi]]) || null;
  const wxProfitV = wxDataV ? wxDataV.profit : 0;
  const wxRevenueV = wxDataV ? wxDataV.revenue : 0;
  const wxCostV = wxDataV ? wxDataV.cost : 0;
  const wxFeeV = wxDataV ? round2(wxDataV.freight + wxDataV.packaging) : 0;
  const pddOpexV = (billSummary[months[vi]] && billSummary[months[vi]].opexDetail) || {};
  const pddFeeV = round2((Number(pddOpexV['运费']) || 0) + (Number(pddOpexV['辅材及其他']) || 0));
  // ===== 全渠道权威汇总（后端 allChannel：拼多多到账 + 微信发货，混合口径，单一数据源）=====
  const ac = (m) => allChannel[m] || { netIncome: 0, costTotal: 0, gross: 0, netProfit: 0, pdd: { netIncome: 0, costTotal: 0, gross: 0, netProfit: 0 }, wx: { paid: 0, revenue: 0, cost: 0, fee: 0, gross: 0, profit: 0 }, tb: { paid: 0, revenue: 0, cost: 0, fee: 0, gross: 0, profit: 0 } };
  const allChannelNetIn = months.map(m => ac(m).netIncome); // 全渠道净入账 = 拼多多到账 + 微信已收
  const allCost = months.map(m => ac(m).costTotal);          // 全渠道商品成本
  const allGross = months.map(m => ac(m).gross);             // 全渠道毛利
  const allProfit = months.map(m => ac(m).netProfit);        // 全渠道净利
  const wxPaidArr = months.map(m => ac(m).wx.paid);
  const wxGrossArr = months.map(m => ac(m).wx.gross);
  const wxProfitArr = months.map(m => ac(m).wx.profit);
  const tbPaidArr = months.map(m => ac(m).tb.paid);
  const tbGrossArr = months.map(m => ac(m).tb.gross);
  const tbProfitArr = months.map(m => ac(m).tb.profit);

  // ===== 数据完整性提醒 =====
  const missingDetails = []; // 未导入账务流水的店铺（店名折叠，不平铺）
  for (const m of months) {
    const c = completeness[m];
    if (c && c.missingCount > 0) missingDetails.push({ month: m, count: c.missingCount, shops: c.missingShops });
  }
  const hasAlert = missingDetails.length > 0;

  // ===== 单品统计（订单发货口径，独立于账单）=====
  const prodList = products.map(p => {
    const arr = (p['收入'] || []);
    const n = arr.length;
    const i = Math.max(0, n - 1);
    const rev = p['收入'][i] || 0, ret = p['退款'][i] || 0, cost = p['商品成本'][i] || 0;
    const gm = rev - ret - cost;
    const prevGm = i > 0 ? ((p['收入'][i - 1] || 0) - (p['退款'][i - 1] || 0) - (p['商品成本'][i - 1] || 0)) : 0;
    const growth = prevGm === 0 ? 0 : (gm - prevGm) / Math.abs(prevGm);
    return { name: p['产品'], spec_code: p.spec_code, rev, ret, cost, gm, rate: rev ? gm / rev : 0, growth };
  });
  const upList = prodList.filter(x => x.growth > 0).sort((a, b) => b.growth - a.growth);
  const downList = prodList.filter(x => x.growth < 0).sort((a, b) => a.growth - b.growth);
  const lastOrderMonth = orderMonths.length ? orderMonths[orderMonths.length - 1] : '';

  // ===== 月度对比 =====
  // 可选月份 = 订单发货月（比财务月更全，未录账单的月财务指标显示「未录」）
  const cmpOptions = orderMonths.map(m => ({ value: m, label: m }));
  const finOf = (m) => billSummary[m] || null; // 拼多多口径（净入账等），未录账单则 null
  const acFinOf = (m) => allChannel[m] || null; // 全渠道口径（拼多多到账 + 微信发货），未录则 null
  const monthIdx = (m) => orderMonths.indexOf(m);
  const prodGm = (m) => {
    const i = monthIdx(m);
    if (i < 0) return [];
    return products.map(p => {
      const rev = p['收入'][i] || 0, ret = p['退款'][i] || 0, cost = p['商品成本'][i] || 0;
      return { name: p['产品'], spec_code: p.spec_code, rev, gm: rev - ret - cost };
    }).filter(x => x.rev !== 0 || x.gm !== 0).sort((a, b) => b.gm - a.gm);
  };
  // 财务指标对比行
  const finRows = [
    ['净入账(全渠道)', 'netIncome'],
    ['商品成本(全渠道)', 'costTotal'],
    ['毛利(全渠道)', 'gross'],
    ['净利(全渠道)', 'netProfit'],
    ['推广费(拼多多)', 'adSpendUsed'],
  ];
  const cmpFinA = acFinOf(cmpA), cmpFinB = acFinOf(cmpB);
  const cmpFinPddA = finOf(cmpA), cmpFinPddB = finOf(cmpB);
  const finCompare = finRows.map(([label, field]) => {
    const isPdd = field === 'adSpendUsed';
    const A = isPdd ? cmpFinPddA : cmpFinA;
    const B = isPdd ? cmpFinPddB : cmpFinB;
    const a = A ? A[field] : null;
    const b = B ? B[field] : null;
    const diff = (a != null && b != null) ? round2(b - a) : null;
    const rate = (a != null && b != null && a !== 0) ? (b - a) / Math.abs(a) : (a != null && b != null && a === 0 ? (b === 0 ? 0 : null) : null);
    return { label, a, b, diff, rate };
  });
  const cmpTopA = prodGm(cmpA).slice(0, 10);
  const cmpTopB = prodGm(cmpB).slice(0, 10);
  const finCell = (v) => (v == null ? <span style={{ color: '#bfbfbf' }}>未录</span> : fmtYuan(v));
  const diffCell = (v) => (v == null ? <span style={{ color: '#bfbfbf' }}>—</span> : <span style={{ color: v >= 0 ? CORAL : C.green, fontWeight: 600 }}>{fmtYuan(v)}</span>);
  const rateCell = (v) => (v == null ? <span style={{ color: '#bfbfbf' }}>—</span> : <span style={{ color: v >= 0 ? CORAL : C.green, fontWeight: 600 }}>{v >= 0 ? '+' : ''}{pct(v)}</span>);

  // ===== 支出结构（跟随查看月份）=====
  const lmLast = ledgerMonths[months[vi]] || DEFAULT_LEDGER;
  const opexDetail = (billSummary[months[vi]] && billSummary[months[vi]].opexDetail) || {};
  // 经营费用（运费/人工/房租/税费/辅材），推广费已在 expense 单独列出；税费即使为 0 也显示（2026-09-26 ACCIO 审查项3）
  const opexEntries = Object.keys(opexDetail).filter(k => k !== '推广费' && ((Number(opexDetail[k]) || 0) !== 0 || k === '税费'));
  const wxAc = ac(months[vi]).wx;
  const tbAc = ac(months[vi]).tb;
  const expense = [
    ['商品成本(拼多多)', costArr[vi], C.gray],
    ['微信商品成本', wxAc.cost, C.teal],
    ['微信运费辅材', wxAc.fee, C.green],
    ['淘宝商品成本', tbAc.cost, C.purple],
    ['淘宝运费辅材', tbAc.fee, C.green],
    ['推广费(拼多多)', adArr[vi], C.amber],
    ['技术服务费', tech[vi], C.blue],
    ['退款', refund[vi], C.red],
    ['支付手续费', pay[vi], C.purple],
    ['罚款赔付', penalty[vi], C.red],
  ];
  opexEntries.forEach((k, idx) => expense.push([k, Number(opexDetail[k]) || 0, [C.blue, C.green, C.teal, C.purple, C.amber][idx % 5]]));
  const totalExp = expense.reduce((s, e) => s + e[1], 0);

  // ===== 资产负债 / 资金 / 现金流 / 风险 =====
  const bs = lmLast.balanceSheet || {};
  const sum = (o) => Object.values(o || {}).reduce((s, v) => s + (Number(v) || 0), 0);
  const ca = sum(bs['流动资产']), nca = sum(bs['非流动资产']), cl = sum(bs['流动负债']), ncl = sum(bs['非流动负债']), eq = sum(bs['所有者权益']);
  const ta = ca + nca, tl = cl + ncl;
  const cf = lmLast.cashFlow || {};
  const fcf = (Number(cf['经营活动现金流净额']) || 0) + (Number(cf['投资活动现金流净额']) || 0);
  const wc = ca - cl;
  const inv = (bs['流动资产'] && bs['流动资产']['存货']) || 0;
  const cur = cl ? ca / cl : 0;
  const quick = cl ? (ca - inv) / cl : 0;
  const lev = ta ? tl / ta : 0;
  const cashCover = cl ? (Number(cf['经营活动现金流净额']) || 0) / cl : 0;
  const level = (v, good, warn) => (v >= good ? 'ok' : (v >= warn ? 'warn' : 'bad'));
  const level2 = (v, good, warn) => (v <= good ? 'ok' : (v <= warn ? 'warn' : 'bad'));
  const risks = [
    { name: '流动比率', val: cur.toFixed(2), ref: '参考 ≥2', lv: level(cur, 2, 1) },
    { name: '速动比率', val: quick.toFixed(2), ref: '参考 ≥1', lv: level(quick, 1, 0.5) },
    { name: '资产负债率', val: pct(lev), ref: '参考 ≤50%', lv: level2(lev, 0.5, 0.7) },
    { name: '现金保障倍数', val: cashCover.toFixed(2), ref: '参考 ≥1', lv: level(cashCover, 1, 0.5) },
  ];
  const badCount = risks.filter(r => r.lv === 'bad').length;
  const warnCount = risks.filter(r => r.lv === 'warn').length;
  const verdict = badCount > 0
    ? { text: '存在高风险信号，建议优先改善现金流保障与短期偿债能力，控制借款规模。', color: '#FFF1F0', border: '#CF1322' }
    : warnCount > 0
      ? { text: '整体可控，但部分指标进入关注区间，建议跟踪负债率与现金保障情况。', color: '#FFF7E6', border: '#FA8C16' }
      : { text: '财务结构健康，各项风险指标均在安全区间。', color: '#F6FFED', border: '#3F8600' };

  // ===== 图表 option =====
  const baseAxis = { axisLine: { lineStyle: { color: '#E8E0D8' } }, axisLabel: { color: '#A08C82', fontSize: 11 } };
  const optProfit = {
    color: [C.blue, C.amber, C.red, C.green],
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 10, right: 10, top: 20, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: months, ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => wan(v) } },
    series: [
      { name: '净入账(拼多多)', type: 'line', data: netIn, smooth: true, lineStyle: { type: 'dashed', width: 1.5 }, itemStyle: { color: C.blue }, areaStyle: { opacity: 0.04 } },
      { name: '全渠道净入账', type: 'line', data: allChannelNetIn, smooth: true, lineStyle: { width: 2.5 }, itemStyle: { color: C.amber }, areaStyle: { opacity: 0.08 } },
      { name: '全渠道毛利', type: 'line', data: allGross, smooth: true, itemStyle: { color: C.red } },
      { name: '全渠道净利', type: 'line', data: allProfit, smooth: true, itemStyle: { color: C.green } },
    ],
  };
  const optGrowth = {
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 10, right: 10, top: 20, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: months, ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => pct(v) } },
    series: [
      { name: '全渠道净入账环比', type: 'bar', data: grow(allChannelNetIn), itemStyle: { color: C.amber } },
      { name: '全渠道净利环比', type: 'bar', data: grow(allProfit), itemStyle: { color: C.green } },
    ],
  };
  const optExpense = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { orient: 'vertical', right: 0, top: 'middle', textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['42%', '70%'], center: ['40%', '50%'],
      data: expense.map(e => ({ name: e[0], value: e[1], itemStyle: { color: e[2] } })),
      label: { show: false },
    }],
  };
  const optBalance = {
    color: [C.blue, C.teal, C.amber, C.red, C.green],
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 10, right: 10, top: 20, bottom: 40, containLabel: true },
    xAxis: { type: 'category', data: ['资产', '负债+权益'], ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => wan(v) } },
    series: [
      { name: '流动资产', type: 'bar', stack: 'a', data: [ca, 0] },
      { name: '非流动资产', type: 'bar', stack: 'a', data: [nca, 0] },
      { name: '流动负债', type: 'bar', stack: 'b', data: [0, cl] },
      { name: '非流动负债', type: 'bar', stack: 'b', data: [0, ncl] },
      { name: '所有者权益', type: 'bar', stack: 'b', data: [0, eq] },
    ],
  };
  const optOperating = {
    tooltip: { trigger: 'axis' },
    grid: { left: 10, right: 10, top: 20, bottom: 30, containLabel: true },
    xAxis: { type: 'category', data: Object.keys(bs['流动资产'] || {}), ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => wan(v) } },
    series: [{ type: 'bar', data: Object.values(bs['流动资产'] || {}), itemStyle: { color: [C.blue, C.teal, C.purple, C.amber] } }],
  };
  const optWorking = {
    tooltip: { trigger: 'axis' },
    grid: { left: 10, right: 10, top: 20, bottom: 30, containLabel: true },
    xAxis: { type: 'category', data: ['流动资产', '流动负债', '流动资金'], ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => wan(v) } },
    series: [{ type: 'bar', data: [ca, cl, wc], itemStyle: { color: [C.blue, C.amber, wc >= 0 ? C.green : C.red] } }],
  };
  const optCash = {
    tooltip: { trigger: 'axis' },
    grid: { left: 10, right: 10, top: 20, bottom: 30, containLabel: true },
    xAxis: { type: 'category', data: ['经营现金流', '投资现金流', '筹资现金流', '自由现金流'], ...baseAxis },
    yAxis: { type: 'value', ...baseAxis, axisLabel: { ...baseAxis.axisLabel, formatter: v => wan(v) } },
    series: [{ type: 'bar', data: [cf['经营活动现金流净额'] || 0, cf['投资活动现金流净额'] || 0, cf['筹资活动现金流净额'] || 0, fcf], itemStyle: { color: [C.blue, C.red, C.amber, fcf >= 0 ? C.green : C.red] } }],
  };

  // ===== 公司账本表单组件 =====
  const setBs = (group, key, v) => setLedgerForm(f => ({ ...f, balanceSheet: { ...f.balanceSheet, [group]: { ...f.balanceSheet[group], [key]: v } } }));
  // 上月未付联动：改「上月未付」→ 应付账款合计 = 上月未付 + 本月入库（分开做）
  const setOpeningPayable = (v) => setLedgerForm(f => {
    const op = (v == null || v === '') ? 0 : Number(v);
    const inbound = ledgerAuto ? (Number(ledgerAuto.monthInboundAmount) || 0) : 0;
    return {
      ...f,
      openingPayable: op,
      balanceSheet: { ...f.balanceSheet, '流动负债': { ...f.balanceSheet['流动负债'], '应付账款': Math.round((op + inbound) * 100) / 100 } },
    };
  });
  const setCf = (key, v) => setLedgerForm(f => ({ ...f, cashFlow: { ...f.cashFlow, [key]: v } }));
  // 经营现金流倒挤 = 期末现金 − 期初 − 投资 − 筹资
  const recalcOpCF = (cf) => ({ ...cf, '经营活动现金流净额': Math.round(((Number(cf['期末现金']) || 0) - (Number(cf['期初现金']) || 0) - (Number(cf['投资活动现金流净额']) || 0) - (Number(cf['筹资活动现金流净额']) || 0)) * 100) / 100 });
  // 期末现金联动：货币资金同步 + 经营现金流重算
  const setClosing = (v) => setLedgerForm(f => {
    const nv = v == null ? 0 : v;
    return {
      ...f,
      cashFlow: recalcOpCF({ ...f.cashFlow, '期末现金': nv }),
      balanceSheet: { ...f.balanceSheet, '流动资产': { ...f.balanceSheet['流动资产'], '货币资金': nv } },
    };
  });
  // 期初/投资/筹资改动也重算经营现金流
  const setCfRecalc = (key, v) => setLedgerForm(f => ({ ...f, cashFlow: recalcOpCF({ ...f.cashFlow, [key]: v == null ? 0 : v }) }));

  const numField = (label, value, onChange, allowNegative = true) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ width: 92, fontSize: 12, color: '#5F5E5A', flexShrink: 0 }}>{label}</span>
      <InputNumber
        style={{ flex: 1 }} precision={2} min={allowNegative ? undefined : 0}
        value={value != null && value !== '' ? value : null} onChange={onChange} placeholder="0.00" prefix="¥" size="small"
      />
    </div>
  );

  // ===== 目标净利润测算数据（实时滚动，随 load 更新）=====
  const now = dayjs();
  const latestMonth = months[last] || '';
  const earnData = {
    monthLabel: latestMonth,
    isCurrentMonth: latestMonth === now.format('YYYY-MM'),
    daysElapsed: now.date(),
    daysInMonth: now.daysInMonth(),
    netProfit: allProfit[last] || 0,       // 全渠道净利（拼多多到账 + 微信发货）
    netIncome: allChannelNetIn[last] || 0, // 全渠道净入账
    wxRevenue: wxRevenue,                  // 微信发货收入（未收部分计入销售规模）
    refund: refund[last] || 0,             // 拼多多退款（店铺级 records 口径）
    freight: Number(pddOpex['运费']) || 0, // 拼多多运费
  };

  return (
    <div className="page-container">
      {/* 页头 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>
            <FundOutlined style={{ color: CORAL, marginRight: 8 }} />公司看板
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
            净入账· 毛利 · 净利 · 单品增长 · 营收能力 · 支出 · 资产负债 · 资金 · 风险　|　财务数据月：{months[0]} ~ {months[last]}
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button type="primary" icon={<BookOutlined />} onClick={() => openLedger(months[last])}>月末快照</Button>
          <Button icon={<AimOutlined />} onClick={() => setEarnOpen(true)}>目标测算</Button>
        </Space>
      </div>

      {/* 总家底横幅（并入自「经营总览」：可支配现金 + 仓库货值 + 客户欠款 + 平台待结算） */}
      <Overview embedded />

      {/* 目标跟进看板（实时进度：已完成/还差/时间/预计/建议） */}
      <div style={{ margin: '16px 0' }}><EarnGoalCard /></div>

      {/* 数据完整性提醒 */}
      {hasAlert && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message="数据录入不完整，以下内容会导致看板数字偏低或失真，请到「财务管理 → 账务流水」补录"
          description={
            <div style={{ fontSize: 13 }}>
              {missingDetails.map((d) => (
                <div key={d.month} style={{ margin: '2px 0' }}>
                  {d.month}：{d.count} 家店铺未导入账务流水{' '}
                  <Popover
                    trigger="click"
                    title={`${d.month} 未导入账务流水的店铺`}
                    content={
                      <div style={{ maxWidth: 340, maxHeight: 300, overflow: 'auto' }}>
                        {d.shops.map((s) => <div key={s} style={{ lineHeight: 1.9 }}>{s}</div>)}
                      </div>
                    }
                  >
                    <a style={{ color: '#D85A30' }}>查看店名</a>
                  </Popover>
                </div>
              ))}
            </div>
          }
        />
      )}

      {/* 查看月份选择（KPI / 利润总汇 / 各店明细 / 支出结构 / 资产负债跟随） */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: '#5F5E5A' }}>查看月份：</span>
        <Select value={months[vi]} onChange={setViewMonth} style={{ width: 116 }}
          options={months.slice().reverse().map(m => ({ value: m, label: m }))} />
        {vi !== last && <Tag color="orange">历史月份回看</Tag>}
      </div>

      {/* KPI（全渠道口径：拼多多到账 + 微信客户；小字标注各渠道构成） */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Kpi label={`${months[vi]} 全渠道净入账`} value={fmtYuan(allChannelNetIn[vi])} delta={grow(allChannelNetIn)[vi]} deltaText="较上月"
            sub={<span>拼多多(到账) {fmtYuan(netIn[vi])} + 微信已收 {fmtYuan(wxPaidArr[vi])} + 淘宝已收 {fmtYuan(tbPaidArr[vi])}</span>} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi label={`${months[vi]} 全渠道毛利`} value={fmtYuan(allGross[vi])} delta={grow(allGross)[vi]} deltaText="较上月"
            sub={<span>拼多多(到账) {fmtYuan(gross[vi])} + 微信(发货) {fmtYuan(wxGrossArr[vi])} + 淘宝(发货) {fmtYuan(tbGrossArr[vi])}</span>} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi label={`${months[vi]} 全渠道净利`} value={fmtYuan(allProfit[vi])} delta={grow(allProfit)[vi]} deltaText="较上月"
            sub={<span>拼多多(到账) {fmtYuan(profit[vi])} + 微信(发货) {fmtYuan(wxProfitArr[vi])} + 淘宝(发货) {fmtYuan(tbProfitArr[vi])}</span>} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi label={`${months[vi]} 全渠道毛利环比`} value={grow(allGross)[vi] != null ? pct(grow(allGross)[vi]) : '—'}
            sub={<span>拼多多毛利环比 {grow(gross)[vi] != null ? pct(grow(gross)[vi]) : '—'}</span>} />
        </Col>
      </Row>

      {/* 全渠道利润总汇（拼多多到账 + 微信发货） */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <BoardCard title={`全渠道利润总汇 · ${months[vi]}`} desc="拼多多 = 到账口径（账务流水），微信客户 / 淘宝客户 = 发货口径（订单）；三者口径不同，合计为混合口径、仅供参考。切换顶部「查看月份」可回看历史">
            <Table size="small" rowKey="channel" pagination={false}
              dataSource={[
                { channel: '拼多多（4 店）', income: netIn[vi], cost: costArr[vi], fee: pddFeeV, profit: profit[vi], basis: '到账' },
                { channel: '微信客户', income: wxRevenueV, cost: wxCostV, fee: wxFeeV, profit: wxProfitV, basis: '发货' },
                { channel: '淘宝客户', income: tbAc.revenue, cost: tbAc.cost, fee: tbAc.fee, profit: tbAc.profit, basis: '发货' },
                { channel: '全渠道合计', income: round2(netIn[vi] + wxRevenueV + tbAc.revenue), cost: round2(costArr[vi] + wxCostV + tbAc.cost), fee: round2(pddFeeV + wxFeeV + tbAc.fee), profit: round2(profit[vi] + wxProfitV + tbAc.profit), basis: '混合' },
              ]}
              columns={[
                { title: '渠道', dataIndex: 'channel', render: (v, r) => <span style={{ fontWeight: r.channel === '全渠道合计' ? 700 : 400 }}>{v}</span> },
                { title: '收入', dataIndex: 'income', align: 'right', render: v => v == null ? '—' : fmtYuan(v) },
                { title: '商品成本', dataIndex: 'cost', align: 'right', render: v => v == null ? '—' : fmtYuan(v) },
                { title: '运费辅材', dataIndex: 'fee', align: 'right', render: v => v == null ? '—' : fmtYuan(v) },
                { title: '净利', dataIndex: 'profit', align: 'right', render: v => <span style={{ color: v >= 0 ? CORAL : C.green, fontWeight: 700 }}>{fmtYuan(v)}</span> },
                { title: '口径', dataIndex: 'basis', width: 72, render: v => <Tag color={v === '到账' ? 'blue' : v === '发货' ? 'green' : 'gold'}>{v}</Tag> },
              ]}
              scroll={{ x: 640 }} />
          </BoardCard>
        </Col>
      </Row>

      {/* 月度对比 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <BoardCard title="月度对比" desc="选择两个月，对比财务指标（全渠道口径 = 拼多多到账 + 微信发货，未录的月显示「未录」）与单品毛利表现，红=增长、绿=下滑">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              <span style={{ fontSize: 13, color: '#5F5E5A' }}>对比月：</span>
              <Select value={cmpA} onChange={setCmpA} style={{ width: 110 }} options={cmpOptions} />
              <SwapOutlined style={{ color: '#A08C82' }} />
              <Select value={cmpB} onChange={setCmpB} style={{ width: 110 }} options={cmpOptions} />
            </div>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 8 }}>财务指标对比</div>
                <Table size="small" rowKey="label" pagination={false}
                  dataSource={finCompare.map(r => ({ ...r, key: r.label }))}
                  columns={[
                    { title: '指标', dataIndex: 'label', width: 110 },
                    { title: cmpA, dataIndex: 'a', align: 'right', render: v => finCell(v) },
                    { title: cmpB, dataIndex: 'b', align: 'right', render: v => finCell(v) },
                    { title: '差额', dataIndex: 'diff', align: 'right', render: v => diffCell(v) },
                    { title: '变化率', dataIndex: 'rate', align: 'right', render: v => rateCell(v) },
                  ]}
                  scroll={{ x: 480 }} />
              </Col>
              <Col xs={24} md={12}>
                <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 8 }}>单品毛利 TOP10 对比</div>
                <Row gutter={16}>
                  <Col span={12}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#8C8C8C', marginBottom: 4 }}>{cmpA}</div>
                    {cmpTopA.length === 0 ? <div style={{ fontSize: 12, color: '#A08C82', padding: '8px 0' }}>无数据</div>
                      : cmpTopA.map((x, i) => <RankRow key={'ca' + i} rank={i + 1} name={x.name} value={fmtYuan(x.gm)} valueColor={x.gm >= 0 ? CORAL : C.green} />)}
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#8C8C8C', marginBottom: 4 }}>{cmpB}</div>
                    {cmpTopB.length === 0 ? <div style={{ fontSize: 12, color: '#A08C82', padding: '8px 0' }}>无数据</div>
                      : cmpTopB.map((x, i) => <RankRow key={'cb' + i} rank={i + 1} name={x.name} value={fmtYuan(x.gm)} valueColor={x.gm >= 0 ? CORAL : C.green} />)}
                  </Col>
                </Row>
              </Col>
            </Row>
          </BoardCard>
        </Col>
      </Row>

      {/* 全渠道各店明细（拼多多到账 + 微信发货） */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <BoardCard title="全渠道 · 各店明细" desc={`${months[vi]} 各店净入账 / 商品成本 / 毛利 / 净利润。拼多多 = 到账口径（账务流水），微信客户 / 淘宝客户 = 发货口径（净入账按已收款）；「公共费用」= 人工/房租等公司级开销，合计与顶部 KPI 完全一致`}>
            <Table size="small" rowKey="店铺" pagination={false}
              columns={[
                { title: '店铺', dataIndex: '店铺', render: (v, r) => r.合计 ? <b>{v}</b> : <span>{v}{r.口径 === '发货' ? <Tag color="green" style={{ marginLeft: 6 }}>发货</Tag> : null}</span> },
                { title: '净入账', dataIndex: '净入账', align: 'right', render: (v, r) => <span style={{ color: CORAL, fontWeight: r.合计 ? 700 : 600 }}>{fmtYuan(v)}</span> },
                { title: '商品成本', dataIndex: '商品成本', align: 'right', render: (v, r) => <span style={{ fontWeight: r.合计 ? 700 : 400 }}>{fmtYuan(v)}</span> },
                { title: '毛利', dataIndex: '毛利', align: 'right', render: (v, r) => <span style={{ fontWeight: r.合计 ? 700 : 400 }}>{fmtYuan(v)}</span> },
                { title: '净利润', dataIndex: '净利润', align: 'right', render: (v, r) => <span style={{ color: Number(v) >= 0 ? CORAL : C.green, fontWeight: r.合计 ? 700 : 600 }}>{fmtYuan(v)}</span> },
                { title: '口径', dataIndex: '口径', width: 72, render: (v, r) => r.合计 ? null : <Tag color={v === '发货' ? 'green' : 'blue'}>{v || '到账'}</Tag> },
              ]}
              dataSource={[
                ...records.filter(r => r['月份'] === months[vi]).sort((a, b) => Number(b['净入账']) - Number(a['净入账'])),
                { 店铺: '微信客户', 净入账: wxAc.paid, 商品成本: wxAc.cost, 毛利: wxAc.gross, 净利润: wxAc.profit, 口径: '发货' },
                { 店铺: '淘宝客户', 净入账: tbAc.paid, 商品成本: tbAc.cost, 毛利: tbAc.gross, 净利润: tbAc.profit, 口径: '发货' },
              ].concat((() => {
                // 合计行：拼多多（含公共费用）+ 微信 + 淘宝，与顶部 KPI 同口径（2026-09-26 ACCIO 审查项1）
                const rows = records.filter(r => r['月份'] === months[vi]);
                const s = (f) => Math.round(rows.reduce((acc, r) => acc + (Number(r[f]) || 0), 0) * 100) / 100;
                return [{
                  店铺: '合计（拼多多到账 + 微信/淘宝发货）', 合计: true,
                  净入账: Math.round((s('净入账') + wxAc.paid + tbAc.paid) * 100) / 100,
                  商品成本: Math.round((s('商品成本') + wxAc.cost + tbAc.cost) * 100) / 100,
                  毛利: Math.round((s('毛利') + wxAc.gross + tbAc.gross) * 100) / 100,
                  净利润: Math.round((s('净利润') + wxAc.profit + tbAc.profit) * 100) / 100,
                }];
              })())}
              scroll={{ x: 700 }} />
          </BoardCard>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <BoardCard title="一、整体利润 / 增长" desc="公司全渠道视角：全渠道净入账（金色实线）= 拼多多净入账 + 微信客户已收款；全渠道毛利/净利 = 拼多多(到账) + 微信(发货)；蓝色虚线为拼多多净入账参照">
            <EChart option={optProfit} height={240} />
            <EChart option={optGrowth} height={180} />
          </BoardCard>
        </Col>

        <Col span={24}>
          <BoardCard title="二、单品增长 / 下滑" desc={`按毛利环比增速排序（${lastOrderMonth} vs 上月），红=增长、绿=下滑；各显示前 10，全部单品明细请见「财务分析 → 商品财务明细」`}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ fontSize: 13, fontWeight: 600, color: CORAL, marginBottom: 6 }}><RiseOutlined /> 增长 TOP {Math.min(10, upList.length)}</div>
                {upList.length === 0 ? <div style={{ fontSize: 12, color: '#A08C82', padding: '12px 0' }}>本月无增长单品</div>
                  : upList.slice(0, 10).map((x, i) => <RankRow key={'up' + i} rank={i + 1} name={x.name} value={'+' + pct(x.growth)} valueColor={CORAL} />)}
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.green, marginBottom: 6 }}><FallOutlined /> 下滑 TOP {Math.min(10, downList.length)}</div>
                {downList.length === 0 ? <div style={{ fontSize: 12, color: '#A08C82', padding: '12px 0' }}>本月无下滑单品</div>
                  : downList.slice(0, 10).map((x, i) => <RankRow key={'down' + i} rank={i + 1} name={x.name} value={pct(x.growth)} valueColor={C.green} />)}
              </Col>
            </Row>
          </BoardCard>
        </Col>

        <Col xs={24} md={12}>
          <BoardCard title="三、主要支出费用板块" desc="本月全渠道商品成本（拼多多 + 微信）+ 平台费用 + 公司经营费用构成">
            <EChart option={optExpense} height={240} />
            <Table size="small" rowKey="name" pagination={false} style={{ marginTop: 8 }}
              columns={[
                { title: '费用项', dataIndex: 'n' },
                { title: '金额', dataIndex: 'v', align: 'right', render: v => fmtYuan(v) },
                { title: '占比', dataIndex: 'r', align: 'right', render: v => pct(v) },
              ]}
              dataSource={expense.slice().sort((a, b) => b[1] - a[1]).map(e => ({ n: e[0], v: e[1], r: totalExp ? e[1] / totalExp : 0 }))}
              scroll={{ x: 360 }} />
          </BoardCard>
        </Col>

        <Col xs={24} md={12}>
          <BoardCard title="四、公司资产负债" desc="资产 = 负债 + 所有者权益（在公司账本中按月填写）">
            <EChart option={optBalance} height={180} />
            <Table size="small" rowKey="n" pagination={false} style={{ marginTop: 8 }}
              columns={[
                { title: '项目', dataIndex: 'n' },
                { title: '金额', dataIndex: 'v', align: 'right', render: v => <span style={{ fontWeight: 600 }}>{fmtYuan(v)}</span> },
              ]}
              dataSource={[
                { n: '流动资产', v: ca }, { n: '非流动资产', v: nca }, { n: '总资产', v: ta },
                { n: '流动负债', v: cl }, { n: '非流动负债', v: ncl }, { n: '所有者权益', v: eq },
              ]} scroll={{ x: 300 }} />
          </BoardCard>
        </Col>

        <Col xs={24} md={8}>
          <BoardCard title="五、运营资金" desc="运营资金 = 流动资产合计（日常经营占用的资金）">
            <EChart option={optOperating} height={200} />
            <div style={{ fontSize: 12, color: '#A08C82', marginTop: 8, lineHeight: 1.7 }}>运营资金 = <b style={{ color: INK }}>{fmtYuan(ca)}</b>，其中货币资金 {fmtYuan(bs['流动资产'] && bs['流动资产']['货币资金'])}、微信客户应收 {fmtYuan(bs['流动资产'] && bs['流动资产']['微信客户应收'])}、平台应收 {fmtYuan(bs['流动资产'] && bs['流动资产']['平台应收'])}、存货 {fmtYuan(inv)}。</div>
          </BoardCard>
        </Col>

        <Col xs={24} md={8}>
          <BoardCard title="六、流动资金" desc="流动资金 = 流动资产 − 流动负债">
            <EChart option={optWorking} height={200} />
            <div style={{ fontSize: 12, color: '#A08C82', marginTop: 8, lineHeight: 1.7 }}>流动资金 = {fmtYuan(ca)} − {fmtYuan(cl)} = <b style={{ color: wc >= 0 ? C.green : C.red }}>{fmtYuan(wc)}</b>（{wc >= 0 ? '为正，短期周转资金充足' : '为负，存在短期资金缺口'}）。</div>
          </BoardCard>
        </Col>

        <Col xs={24} md={8}>
          <BoardCard title="七、自有现金流" desc="自由现金流 ≈ 经营现金流净额 + 投资现金流净额">
            <EChart option={optCash} height={200} />
            <div style={{ fontSize: 12, color: '#A08C82', marginTop: 8, lineHeight: 1.7 }}>自由现金流 ≈ <b style={{ color: fcf >= 0 ? C.green : C.red }}>{fmtYuan(fcf)}</b>；期末现金 {fmtYuan(cf['期末现金'])}，期初 {fmtYuan(cf['期初现金'])}。{fcf >= 0 ? '经营造血为正，资金安全' : '自由现金流为负，需关注融资与回款'}。</div>
          </BoardCard>
        </Col>

        <Col span={24}>
          <BoardCard title="八、财务风险" desc="四项核心偿债与杠杆指标，红黄绿预警">
            <Row gutter={12}>
              {risks.map(r => {
                const color = r.lv === 'ok' ? C.green : (r.lv === 'warn' ? C.amber : '#CF1322');
                const bg = r.lv === 'ok' ? '#F6FFED' : (r.lv === 'warn' ? '#FFF7E6' : '#FFF1F0');
                const label = r.lv === 'ok' ? '健康' : (r.lv === 'warn' ? '关注' : '风险');
                return (
                  <Col xs={12} md={6} key={r.name}>
                    <div style={{ border: `1px solid ${color}33`, background: bg, borderRadius: 8, padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: '#8C8C8C' }}>{r.name}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color, margin: '6px 0' }}>{r.val}</div>
                      <Tag color={r.lv === 'ok' ? 'success' : (r.lv === 'warn' ? 'warning' : 'error')} style={{ borderRadius: 999 }}>{label}</Tag>
                      <div style={{ fontSize: 11, color: '#A08C82', marginTop: 4 }}>{r.ref}</div>
                    </div>
                  </Col>
                );
              })}
            </Row>
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: verdict.color, border: `1px solid ${verdict.border}`, fontSize: 13, color: INK, lineHeight: 1.7 }}>
              <b>综合判断：</b>{verdict.text}
            </div>
          </BoardCard>
        </Col>
      </Row>

      {/* ===== 公司账本 · 月末快照 ===== */}
      <Modal
        title={<Space><BookOutlined style={{ color: CORAL }} />公司账本 · 月末快照（{ledgerMonth || ''}）</Space>}
        open={ledgerOpen}
        onCancel={() => setLedgerOpen(false)}
        width={860}
        footer={[
          <Button key="prefill" icon={<ThunderboltOutlined />} onClick={applyPrefill} disabled={!ledgerPrefill}>按建议重填</Button>,
          <Button key="cancel" onClick={() => setLedgerOpen(false)}>取消</Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} loading={savingLedger} onClick={saveLedger}>保存快照</Button>,
        ]}
      >
        {!ledgerForm ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
            <Space style={{ marginBottom: 10 }} wrap>
              <span style={{ fontSize: 13, color: '#5F5E5A' }}>账本月份：</span>
              <DatePicker
                picker="month" allowClear={false}
                value={dayjs(ledgerMonth + '-01')}
                onChange={(d) => { if (d) { setLedgerMonth(d.format('YYYY-MM')); openLedger(d.format('YYYY-MM')); } }}
              />
              {ledgerAuto && <Tag color="orange">本月净利 {fmtYuan(ledgerAuto.monthProfit)}</Tag>}
              {ledgerAuto && <Tag color="blue">累计净利 {fmtYuan(ledgerAuto.cumProfit)}</Tag>}
              {ledgerHasSaved && <Tag color="green">该月已录，可直接修改</Tag>}
            </Space>

            <Divider orientation="left" style={{ margin: '10px 0' }}>① 需要手动填写（仅这几项，其余全自动）</Divider>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={8}>{numField('应付工资', ledgerForm.balanceSheet['流动负债']['应付工资'], v => setBs('流动负债', '应付工资', v))}</Col>
              <Col xs={24} md={8}>{numField('短期借款', ledgerForm.balanceSheet['流动负债']['短期借款'], v => setBs('流动负债', '短期借款', v))}</Col>
              <Col xs={24} md={8}>{numField('长期借款', ledgerForm.balanceSheet['非流动负债']['长期借款'], v => setBs('非流动负债', '长期借款', v))}</Col>
              <Col xs={24} md={8}>{numField('平台应收', ledgerForm.balanceSheet['流动资产']['平台应收'], v => setBs('流动资产', '平台应收', v))}</Col>
              <Col xs={24} md={8}>{numField('实收资本', ledgerForm.balanceSheet['所有者权益']['实收资本'], v => setBs('所有者权益', '实收资本', v))}</Col>
            </Row>
            <div style={{ fontSize: 12, color: '#A08C82', marginTop: 2, lineHeight: 1.7 }}>
              平台应收 = 拼多多后台待结算款；应付工资 / 短期借款 / 长期借款 / 实收资本系统里无数据源，只能手动填（首次填一次后每月结转，基本不用动）。
            </div>

            <Divider orientation="left" style={{ margin: '10px 0' }}>② 自动生成（只读，随系统实时联动）</Divider>
            <Row gutter={[12, 0]}>
              {(() => {
                const ro = (label, val, note) => (
                  <Col xs={24} md={12} lg={8} key={label}>
                    <div style={{ background: '#FAF7F2', border: '1px solid #F0E8DE', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#A08C82' }}>{label}{note ? <span style={{ fontSize: 10 }}> · {note}</span> : null}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: INK, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(val)}</div>
                    </div>
                  </Col>
                );
                return (<>
                  {ro('货币资金（现金）', ledgerForm.balanceSheet['流动资产']['货币资金'], '资金中心实时')}
                  {ro('微信客户应收', ledgerForm.balanceSheet['流动资产']['微信客户应收'], '订单自动')}
                  {ro('存货', ledgerForm.balanceSheet['流动资产']['存货'], '库存×成本')}
                  {ro('应付账款', ledgerForm.balanceSheet['流动负债']['应付账款'], '应付账期+上月结转')}
                  {ro('未分配利润', ledgerForm.balanceSheet['所有者权益']['未分配利润'], '累计净利')}
                  {ro('经营现金流', ledgerForm.cashFlow['经营活动现金流净额'], '期末−期初−投资−筹资')}
                </>);
              })()}
            </Row>
            <div style={{ fontSize: 12, color: '#A08C82', lineHeight: 1.8, background: '#FFF8F4', border: '1px solid #F3E2D8', borderRadius: 8, padding: '8px 14px' }}>
              自动项说明：货币资金 = 资金中心实时余额；应付账款 = 应付账期未付 + 上月结转；存货 = 库存成本合计；微信客户应收 = 未回款订单；未分配利润 = 累计净利。点「按建议重填」一键刷新为最新实时值；如需手动覆盖，展开下方「全部字段」。
            </div>

            <Collapse ghost style={{ marginTop: 2 }}>
              <Collapse.Panel header={<span style={{ fontSize: 12, color: '#8B6914' }}>展开全部字段（固定资产 / 其他负债 / 现金流明细，通常无需改）</span>} key="adv">
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>{numField('期初现金', ledgerForm.cashFlow['期初现金'], v => setCfRecalc('期初现金', v))}</Col>
                  <Col xs={24} md={8}>{numField('投资现金流', ledgerForm.cashFlow['投资活动现金流净额'], v => setCfRecalc('投资活动现金流净额', v))}</Col>
                  <Col xs={24} md={8}>{numField('筹资现金流', ledgerForm.cashFlow['筹资活动现金流净额'], v => setCfRecalc('筹资活动现金流净额', v))}</Col>
                  {['固定资产', '无形资产', '其他非流动资产'].map(k => (
                    <Col xs={24} md={8} key={k}>{numField(k, ledgerForm.balanceSheet['非流动资产'][k], v => setBs('非流动资产', k, v))}</Col>
                  ))}
                  <Col xs={24} md={8}>{numField('其他流动负债', ledgerForm.balanceSheet['流动负债']['其他流动负债'], v => setBs('流动负债', '其他流动负债', v))}</Col>
                  <Col xs={24} md={8}>{numField('其他非流动负债', ledgerForm.balanceSheet['非流动负债']['其他非流动负债'], v => setBs('非流动负债', '其他非流动负债', v))}</Col>
                </Row>
              </Collapse.Panel>
            </Collapse>

            {(() => {
              const sum = (obj) => Object.values(obj || {}).reduce((s, v) => s + (Number(v) || 0), 0);
              const assets = sum(ledgerForm.balanceSheet['流动资产']) + sum(ledgerForm.balanceSheet['非流动资产']);
              const liabEq = sum(ledgerForm.balanceSheet['流动负债']) + sum(ledgerForm.balanceSheet['非流动负债']) + sum(ledgerForm.balanceSheet['所有者权益']);
              const gap = Math.round((assets - liabEq) * 100) / 100;
              const ok = Math.abs(gap) < 0.01;
              return (
                <div style={{ marginTop: 10, padding: '8px 14px', borderRadius: 8, fontSize: 13, background: ok ? '#F6FFED' : '#FFF7E6', border: `1px solid ${ok ? '#B7EB8F' : '#FFD591'}`, color: INK }}>
                  <b>勾稽校验：</b>资产合计 {fmtYuan(assets)}　=　负债 + 权益 {fmtYuan(liabEq)}　
                  {ok ? <span style={{ color: C.green, fontWeight: 700 }}>✓ 平</span> : <span style={{ color: '#D48806', fontWeight: 700 }}>差额 {fmtYuan(gap)}（未录的资产/负债项会体现在差额里，可忽略或补录）</span>}
                </div>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* 目标净利润测算弹窗（数据实时滚动，目标手动输入） */}
      <EarnGoalModal open={earnOpen} onClose={() => setEarnOpen(false)} data={earnData} />
    </div>
  );
}
